import type { ReactNode } from "react";
import CircularLoader from "../../../../../components/common/CircularLoader/CircularLoader";
import StatusBadge from "../../../../../components/common/StatusBadge/StatusBadge";
import TruncateTooltip from "../../../../../components/common/TruncateTooltip/TruncateTooltip";
import type { ChannelCatalogItem, CoverView } from "../../../api/connectionHub.types";
import { CONNECTION_STATUS, CONNECTION_STATUS_PRESENTATION } from "../../../constants/connectionStatus.constants";
import ConnectionLogo from "../../common/ConnectionLogo/ConnectionLogo";
import ConnectionErrorPanel from "../../common/ConnectionErrorPanel/ConnectionErrorPanel";
import styles from "./CoverStatusRow.module.scss";

export interface CoverStatusRowProps {
  cover: CoverView;
  channel?: ChannelCatalogItem;
  message?: string | null;
  error?: string | null;
  spinner?: boolean;
  action?: ReactNode;
}

const CoverStatusRow = ({ cover, channel, message, error, spinner, action }: CoverStatusRowProps): JSX.Element => {
  const status = CONNECTION_STATUS_PRESENTATION[cover.status];

  return (
    <div className={styles.row}>
      <div className={styles.main}>
        <ConnectionLogo logoUrl={channel?.logo ?? cover.logo} className={styles.logo} />
        <div className={styles.text}>
          <TruncateTooltip text={cover.ipName} className={styles.name} />
          {message ? <p className={styles.message}>{message}</p> : null}
        </div>
        {/* Figma shows a bare loader per channel while verifying; the label lives on the inbox badge. */}
        {cover.status === CONNECTION_STATUS.VERIFYING ? (
          <span className={styles.progress} role="status" aria-label={`${cover.ipName} ${status.label}`}>
            {spinner ?? status.spinner ? <CircularLoader width="1.5rem" height="1.5rem" innerSize="62%" /> : null}
          </span>
        ) : (
          <StatusBadge label={status.label} variant={status.variant} spinner={spinner ?? status.spinner} />
        )}
      </div>
      <ConnectionErrorPanel error={error} className={styles.errorPanel} />
      {action ? <div className={styles.action}>{action}</div> : null}
    </div>
  );
};

export default CoverStatusRow;
